import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Menu } from '../../dropdown/Menu';
import { DropdownOption } from '../../dropdown/types';
import { DropdownSize } from '../../dropdown/DropdownSize';
import { MenuItem } from '../../dropdown/MenuItem';
import { ItemLabel } from '../../dropdown/Menu.styled';
import { useDropdownMenu } from '../../dropdown/useDropdownMenu';
import { withCssSelector } from '../../shared/withCssSelector';
import { Loader } from '../../loader';
import { Button } from '../../button';
import { Checkbox } from '../checkbox';
import { Input } from '../input';
import {
	CheckboxMenuRow,
	CountBadge,
	MenuFooter,
	MenuSearchHeader,
	MenuSelectAllHeader,
	RollingDigit,
	RollingDigits,
	StyledChevron,
	SelectionCount,
	SelectionCountSlot,
	StyledPlaceholder,
	StyledSelectInput,
} from './MultiSelectDropdown.style';

// Wide enough for a checkbox and a two-word option without wrapping.
const FILTER_MENU_MIN_WIDTH = 400;

type MultiSelectCheckboxDropdownProps = {
	className?: string;
	options: string[] | DropdownOption<string>[];
	selectedValues: string[];
	/** Called on Apply and on Clear — never while the boxes are being ticked. */
	onChange: (values: string[]) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	loadingPlaceholder?: string;
	noMatchesPlaceholder?: string;
	noOptionsPlaceholder?: string;
	selectAllLabel?: string;
	applyLabel?: string;
	clearLabel?: string;
	disabled?: boolean;
	searchable?: boolean;
	size?: DropdownSize;
	hasError?: boolean;
	loading?: boolean;
};

const checkboxSizeFor = (size: DropdownSize) =>
	size === 'large' || size === 'default' ? 'large' : 'compact';

// The checkbox rows are the rows a Menu already draws, wearing a <label> so the
// native control does the toggling — and the keyboard reaches it — without a key
// handler of our own. `data-value` is what the menu's hover rectangle tracks, so
// these rows light up like any other.
const CheckboxRow = ({ value, label, checked, indeterminate, onToggle, size, leading, trailing }: {
	value: string;
	label: string;
	leading?: React.ReactNode;
	checked: boolean;
	indeterminate?: boolean;
	onToggle: () => void;
	size: DropdownSize;
	trailing?: React.ReactNode;
}) => <CheckboxMenuRow
	as={'label'}
	$size={size}
	$selected={checked}
	data-value={value}>
	<Checkbox
		checked={checked}
		indeterminate={indeterminate}
		onChange={onToggle}
		size={checkboxSizeFor(size)} />
	{leading}
	<ItemLabel>{label}</ItemLabel>
	{trailing}
</CheckboxMenuRow>;

// The count swaps rather than redraws: the number going out travels the way the
// count moved, and its replacement arrives from the other side. The one leaving
// is held in state, not a ref, so an unrelated re-render cannot cut it short —
// it is dropped when its own animation ends.
const RollingCount = ({ value }: { value: number }) => {
	const [leaving, setLeaving] = useState<number | null>(null);
	const shown = useRef(value);
	const rising = useRef(true);

	if (shown.current !== value) {
		rising.current = value > shown.current;
		setLeaving(shown.current);
		shown.current = value;
	}

	return <RollingDigits>
		{leaving !== null && <RollingDigit
			key={leaving}
			$leaving
			$rising={rising.current}
			aria-hidden
			onAnimationEnd={() => setLeaving(null)}>{leaving}</RollingDigit>}
		<RollingDigit key={value} $rising={rising.current}>{value}</RollingDigit>
	</RollingDigits>;
};

export const MultiSelectCheckboxDropdown = withCssSelector(
	React.forwardRef<HTMLDivElement, MultiSelectCheckboxDropdownProps>(
		function MultiSelectCheckboxDropdown({
			className,
			options,
			selectedValues,
			onChange,
			placeholder,
			searchPlaceholder,
			loadingPlaceholder,
			noMatchesPlaceholder = '',
			noOptionsPlaceholder = '',
			selectAllLabel = 'Select all',
			applyLabel = 'Apply',
			clearLabel = 'Clear filters',
			disabled,
			searchable = false,
			size = 'compact',
			hasError = false,
			loading = false,
		}, ref) {
			const [searchQuery, setSearchQuery] = useState('');
			// The ticks are a draft until Apply commits them, so the boxes need
			// somewhere to live that is not the caller's state.
			const [draft, setDraft] = useState(selectedValues);

			const { isOpen, onClose, onOpen, triggerRef, menuProps, renderMenu } =
				useDropdownMenu<HTMLDivElement>({
					sticky: true,
					triggerRef: ref,
					onCollapsed: () => setSearchQuery(''),
				});

			const normalizedOptions: DropdownOption<string>[] = useMemo(() =>
				options.map(option => typeof option === 'string'
					? { value: option, label: option }
					: option),
			[options]);

			// Unlike MultiSelectDropdown, ticked options stay on the list — the box is
			// what carries the selection, so dropping the row would hide it.
			const visibleOptions = useMemo(() => searchable && searchQuery
				? normalizedOptions.filter(option =>
					option.label.toLowerCase().includes(searchQuery.toLowerCase()))
				: normalizedOptions,
			[normalizedOptions, searchable, searchQuery]);

			const toggle = (value: string) => setDraft(current => current.includes(value)
				? current.filter(v => v !== value)
				: [...current, value]);

			// Select all governs what the search left on screen, not the whole list —
			// the count beside it says how many rows that is.
			const visibleValues = visibleOptions.map(option => option.value);
			const selectedVisible = visibleValues.filter(value => draft.includes(value));
			const allVisibleSelected = visibleValues.length > 0
				&& selectedVisible.length === visibleValues.length;

			const toggleAll = () => setDraft(current => allVisibleSelected
				? current.filter(value => !visibleValues.includes(value))
				: [...current, ...visibleValues.filter(value => !current.includes(value))]);

			// The draft is what the effect below commits, and the effect runs after
			// the render that closed the menu — a ref is what has the current ticks
			// by then without the effect re-running on every one of them.
			const draftRef = useRef(draft);
			draftRef.current = draft;
			// Armed while the menu is open; a close that is not Apply or Clear —
			// clicking outside, or the trigger again — commits on the way out.
			const pendingCommit = useRef(false);

			useEffect(() => {
				if (isOpen) {
					pendingCommit.current = true;
					return;
				}
				if (!pendingCommit.current) {
					return;
				}
				pendingCommit.current = false;
				onChange(draftRef.current);
			// The close is the trigger; onChange is re-made every render at most
			// call sites and would fire this on an unrelated re-render.
			// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [isOpen]);

			const onTriggerClick = () => {
				if (disabled || loading) {
					return;
				}
				if (isOpen) {
					onClose();
					return;
				}
				// Reopening starts from what is committed — nothing is in flight,
				// since every close commits.
				setDraft(selectedValues);
				onOpen();
			};

			// Closing the menu keeps the ticks rather than dropping them, so the
			// only way out that discards anything is Clear. Apply and Clear commit
			// here and disarm the close, so the caller hears one change per menu.
			const commit = (values: string[]) => {
				pendingCommit.current = false;
				setDraft(values);
				onChange(values);
				onClose();
			};

			// The trigger counts what is picked instead of naming it: a filter bar
			// sits several of these side by side, and a list of labels there is
			// either truncated or as wide as the menu.
			const triggerText = loading ? loadingPlaceholder : placeholder;
			const selectionCount = isOpen ? draft.length : selectedValues.length;
			// The badge slides away carrying the number it had: rolling a 0 in
			// under a pill that is already leaving is motion nobody can read.
			const lastCount = useRef(selectionCount);
			if (selectionCount > 0) {
				lastCount.current = selectionCount;
			}

			return <>
				<StyledSelectInput
					onClick={onTriggerClick}
					$disabled={disabled}
					$engaged={isOpen && !searchable}
					$open={isOpen}
					$size={size}
					$hasError={hasError}
					className={className}
					ref={triggerRef}
					// Reachable and operable from the keyboard, like the input it draws as.
					tabIndex={disabled ? -1 : 0}>
					{/* Open, the badge counts ticks as they land; closed, what the
						caller holds. Every close commits, so they agree. */}
					<SelectionCountSlot $visible={!loading && selectionCount > 0}>
						<SelectionCount>
							<RollingCount value={lastCount.current} />
						</SelectionCount>
					</SelectionCountSlot>
					{triggerText ? <StyledPlaceholder>{triggerText}</StyledPlaceholder> : null}
					{loading ? <Loader /> : <StyledChevron icon={'dropdown-chevron'} />}
				</StyledSelectInput>
				{renderMenu(
					<Menu
						{...menuProps}
						size={size}
						// A filter trigger is narrow; its options are not.
						minWidth={FILTER_MENU_MIN_WIDTH}
						header={<>
							{searchable && <MenuSearchHeader>
								<Input
									size={size}
									value={searchQuery}
									onChange={e => setSearchQuery(e.target.value)}
									placeholder={searchPlaceholder ?? 'Search'}
									autoFocus />
							</MenuSearchHeader>}
							{visibleOptions.length > 0 && <MenuSelectAllHeader>
								<CheckboxRow
									value={'__select_all__'}
									label={selectAllLabel}
									checked={allVisibleSelected}
									indeterminate={!allVisibleSelected && selectedVisible.length > 0}
									onToggle={toggleAll}
									size={size}
									trailing={<CountBadge>{visibleOptions.length}</CountBadge>} />
							</MenuSelectAllHeader>}
						</>}
						items={visibleOptions.length === 0
							? <MenuItem
								label={searchQuery ? noMatchesPlaceholder : noOptionsPlaceholder}
								disabled
								value={''}
								size={size} />
							: visibleOptions.map(option => <CheckboxRow
								key={option.value}
								value={option.value}
								label={option.label}
								leading={option.leading}
								checked={draft.includes(option.value)}
								onToggle={() => toggle(option.value)}
								size={size} />)}
						footer={<MenuFooter>
							<Button
								variant={'outline'}
								size={'compact'}
								label={clearLabel}
								onClick={() => commit([])} />
							<Button
								variant={'strong'}
								size={'compact'}
								label={applyLabel}
								onClick={() => commit(draft)} />
						</MenuFooter>} />
				)}
			</>;
		})
	, StyledSelectInput.toString());
